import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { LogIn, LineChart, Wallet, Check, TrendingUp } from "lucide-react";

const stepIcons = [LogIn, LineChart, Wallet];

export default function PortalDemo() {
  const { t } = useLang();
  const p = t.portal;

  return (
    <section id="portal" data-testid="portal-section" className="relative bg-icdh-surface py-28 md:py-40 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left: narrative + steps */}
        <div>
          <Reveal><Overline>{p.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-icdh-navy">{p.title}</h2>
          </Reveal>
          <Reveal delay={0.2}><p className="mt-8 text-icdh-navy/70 text-lg max-w-md">{p.lead}</p></Reveal>

          <p className="mt-12 text-xs uppercase tracking-[0.24em] text-icdh-navy/50">{p.steps_title}</p>
          <div className="mt-6 space-y-px">
            {p.steps.map((st, i) => {
              const Icon = stepIcons[i] || LogIn;
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div data-testid={`portal-step-${i}`} className="flex items-start gap-5 py-5 border-t border-icdh-navy/15">
                    <span className="h-11 w-11 flex-shrink-0 flex items-center justify-center bg-icdh-navy text-white"><Icon size={20} strokeWidth={1.6} /></span>
                    <div>
                      <h3 className="font-sans font-bold text-icdh-navy">{st.t}</h3>
                      <p className="text-sm text-icdh-navy/65 mt-1">{st.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Right: device mockup */}
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="absolute -inset-4 bg-icdh-red/5 blur-2xl" />
            <div className="relative bg-icdh-navy text-white p-6 md:p-8 shadow-2xl">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-icdh-red" />
                  <span className="text-xs uppercase tracking-[0.2em] text-white/60">{p.device_label}</span>
                </div>
                <span className="text-xs text-emerald-400 flex items-center gap-1"><TrendingUp size={14} />Live</span>
              </div>

              {/* Grades */}
              <div className="mt-6">
                <p className="text-sm font-bold text-white/80">{p.grades_title}</p>
                <div className="mt-3 space-y-2">
                  {p.grades.map((g, i) => (
                    <div key={i} data-testid={`portal-grade-${i}`} className="flex items-center justify-between bg-white/5 px-4 py-3">
                      <span className="text-sm text-white/80">{g.s}</span>
                      <span className="font-serif text-xl text-emerald-400">{g.g}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attendance */}
              <div className="mt-4 flex items-center justify-between bg-white/5 px-4 py-3">
                <span className="text-sm text-white/70">{p.attendance}</span>
                <span className="text-sm text-emerald-400 flex items-center gap-2"><Check size={16} />{p.present}</span>
              </div>

              {/* Payment card */}
              <div className="mt-6 border border-white/10 p-5">
                <p className="text-sm font-bold text-white/80">{p.pay_title}</p>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-white/50">{p.pay_amount}</p>
                    <p className="font-serif text-3xl">$450<span className="text-base text-white/50"> USD</span></p>
                  </div>
                  <button data-testid="portal-pay-btn" className="h-12 px-6 bg-[#6d1ed4] hover:bg-[#5a17b0] text-white text-sm font-bold tracking-wide transition-colors">
                    {p.pay_with}
                  </button>
                </div>
                <p className="mt-3 text-[11px] text-white/40">{p.pay_note}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
